import React, { useState, useEffect, useContext, createContext } from 'react';
import { MemoryRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
    Container, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    CircularProgress, 
    Alert,
    Paper,  
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
} from '@mui/material';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { useAuth } from '../../../hooks/useAuth';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SpinningCustomLogo from '../../Common/SpinningCustomLogo'; 


export default function VerificationModal({ open, onSuccess }) {
    const { user } = useAuth();
    const { requestCode, verifyCode, isLoading } = useEmailVerification();

    const [code, setCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);


    useEffect(() => {
        let timer;
        if (resendCooldown > 0) {
            timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleRequestCode = async () => {
        setError(null);
        try {           
            await requestCode(user.email);
            setCodeSent(true);
            setResendCooldown(60); 
        } catch (err) {
            setError(err.message);
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) return; 
        await handleRequestCode(); 
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            await verifyCode(user.email, code);
            setIsSuccess(true);
            onSuccess(); 
        } catch (err) {
            setError(err.message || 'The code you entered is incorrect.');
        }
    };

    return (
        <Dialog open={open} PaperProps={{ sx: { borderRadius: '28px', width: '100%', maxWidth: '420px', margin: '16px' } }}>
            <DialogContent sx={{ p: '24px', minHeight: '320px', boxSizing: 'border-box' }}>
                {isSuccess ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} sx={{ height: '100%'}}>
                        <CheckCircleOutlineIcon sx={{ fontSize: '3.5rem', color: 'success.main' }} />
                        <Typography component="h1" variant="h5" sx={{ fontWeight: 500 }}>All Set!</Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">Your email has been verified successfully.</Typography>
                    </Box>
                ) : (
                    <>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{ mb: 2 }}>
                            <ShieldOutlinedIcon sx={{ fontSize: '2.5rem', color: 'primary.main' }} />
                            <Typography component="h1" variant="h5" sx={{ fontWeight: 500 }}>Confirm it's you</Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {codeSent 
                                    ? `For your security, we've sent a code to ${user?.email}. Please enter it.`
                                    : 'To continue, we need to make sure this email belongs to you.'
                                }
                            </Typography>
                        </Box>
                        <Box sx={{ mt: '24px', width: '100%' }}>
                            {!codeSent ? (
                                <Button fullWidth variant="contained" onClick={handleRequestCode} disabled={isLoading} sx={{ py: '12px', textTransform: 'none', fontSize: '0.9rem', borderRadius: '999px' }}>
                                    {isLoading ? <SpinningCustomLogo size={24} color="inherit" /> : 'Get verification code'}
                                </Button>
                            ) : (
                                <Box component="form" onSubmit={handleVerifyCode} noValidate>
                                    <TextField variant="outlined" margin="normal" required fullWidth id="code"
                                        label="Enter code" name="code" autoComplete="one-time-code" autoFocus
                                        value={code} onChange={(e) => setCode(e.target.value)}
                                        inputProps={{ maxLength: 6, style: { letterSpacing: '0.5em', textAlign: 'center' } }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '24px' }}>
                                        <Button 
                                            onClick={handleResendCode} 
                                            disabled={resendCooldown > 0}
                                            sx={{ textTransform: 'none', color: 'text.secondary' }}
                                        >
                                            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                                        </Button>
                                        <Button type="submit" variant="contained" sx={{ textTransform: 'none', px: 3, py: '10px', borderRadius: '999px' }} disabled={isLoading || !code || code.length < 6}>
                                            {isLoading ? <SpinningCustomLogo size={22} color="inherit"/> : 'Confirm'}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            {error && <Alert severity="error" sx={{ mt: 2, width: '100%', borderRadius: 2 }}>{error}</Alert>}
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}